import { useCallback, useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, HeadlineXS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';

import type { OfferBuilderBuildingPage_publicUserInfoInterface } from '#src/components/directOffer/NewOfferBuilder/BuildingPage/__generated__/index.graphql.ts';
import { setDuration } from '#src/components/directOffer/NewOfferBuilder/actions.ts';
import type {
  CardDataType,
  StateProps,
} from '#src/components/directOffer/NewOfferBuilder/types.ts';

const Wrapper = styled(SBHorizontal)`
  padding: var(--double-unit);
`;
const messages = defineMessages({
  duration: {
    defaultMessage: 'Offer duration',
  },
  nbDays: {
    defaultMessage: '{nbDays, plural, one {# day} other {# days}}',
  },
  answersIn: {
    defaultMessage:
      '{nickname} typically answers in <bold>less than {hours, plural, one {# hour} other {# hours}}</bold>.',
  },
});

const Subtitle = styled(BodyS)`
  color: var(--c-nd-600);
`;

const allowedDurations = [1, 2, 3, 4, 5, 6, 7] as const;

type Duration = (typeof allowedDurations)[number];

const defaultDuration: Duration = 7;

type OptionType = { label: string; value: Duration };

export const InputDuration = <D extends CardDataType>({
  state,
  dispatch,
  to,
}: StateProps<D> & {
  to: OfferBuilderBuildingPage_publicUserInfoInterface;
}) => {
  const { formatMessage } = useIntl();

  const { duration } = state;

  const options = useMemo<OptionType[]>(
    () =>
      allowedDurations.map(nbDays => ({
        label: formatMessage(messages.nbDays, { nbDays }),
        value: nbDays,
      })),
    [formatMessage]
  );

  const onChange = useCallback(
    (newValue: Duration) => {
      if (newValue) {
        dispatch(setDuration(newValue));
      } else {
        dispatch(setDuration(defaultDuration));
      }
    },
    [dispatch]
  );

  return (
    <Wrapper>
      <HeadlineXS as="h6">
        <FormattedMessage {...messages.duration} />
        {!!to.hoursToAnswerTrades && (
          <Subtitle>
            <FormattedMessage
              {...messages.answersIn}
              values={{
                bold: Bold,
                nickname: to.nickname,
                hours: to.hoursToAnswerTrades,
              }}
            />
          </Subtitle>
        )}
      </HeadlineXS>
      <DropdownSelect
        size="medium"
        value={String(duration)}
        options={options.map(o => ({
          value: String(o.value),
          label: o.label,
        }))}
        onChange={value => {
          onChange(+value as Duration);
        }}
      />
    </Wrapper>
  );
};
